import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Download, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  downloadSubscriptionInvoicePdf,
  listSubscriptionInvoices,
  type SubscriptionInvoiceRow,
} from "@/services/subscriptionInvoicesService";

const QK = {
  list: ["subscription-invoices"],
};

function formatDate(iso: string): string {
  const d = iso.slice(0, 10).split("-");
  if (d.length !== 3) return iso;
  return `${d[2]}/${d[1]}/${d[0]}`;
}

function formatAmount(minor: number, currency: string): string {
  const major = minor / 100;
  return `${currency} ${major.toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

type StatusVariant = "default" | "secondary" | "outline" | "destructive";

function resolveStatus(row: SubscriptionInvoiceRow): {
  labelKey: string;
  variant: StatusVariant;
} {
  if (row.invoiceStatus === "invoice_failed") {
    return { labelKey: "settings.plans.history.statusFailed", variant: "destructive" };
  }
  if (row.invoiceStatus === "pending_invoice" || !row.invoice) {
    return {
      labelKey: "settings.plans.history.statusPendingInvoice",
      variant: "secondary",
    };
  }
  const dgi = row.invoice.dgiStatus;
  if (dgi === "rejected") {
    return {
      labelKey: "settings.plans.history.statusInvoiceRejected",
      variant: "destructive",
    };
  }
  if (dgi === "confirmed") {
    return {
      labelKey: "settings.plans.history.statusInvoiceConfirmed",
      variant: "default",
    };
  }
  return {
    labelKey: "settings.plans.history.statusInvoiceIssuedSubmittingToDgi",
    variant: "outline",
  };
}

export function SubscriptionInvoicesTable(): JSX.Element {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: QK.list,
    queryFn: listSubscriptionInvoices,
    refetchInterval: 60_000,
  });

  /**
   * ONLY DGI-CONFIRMED COMPROBANTES ARE LISTED.
   *
   * The in-flight states are ours, not the tenant's: "Enviando a DGI" and a
   * failed or rejected submission are issuer problems that only we can act on,
   * and showing a tenant "Rechazada" on a comprobante for money they already
   * paid alarms them about something they cannot fix. A row appears here once
   * it is a valid fiscal document and not before.
   *
   * THE TRADE: a rejection is now invisible in the product, so it has to be
   * caught on our side — `subscription_charge.lastError` and `invoice.dgiStatus
   * = 'rejected'` are the signals, and nothing here will surface them.
   */
  const allRows = data ?? [];
  const rows = allRows.filter((r) => r.invoice?.dgiStatus === "confirmed");
  // A charge exists but its CFE is not confirmed yet. The default empty copy
  // says no payment has been made, which would be a lie to someone who just
  // paid, so the in-flight case gets its own line.
  const hasUnconfirmed = allRows.length > rows.length;

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-foreground">
          {t("settings.plans.history.title")}
        </CardTitle>
        <CardDescription>
          {t("settings.plans.history.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-8 text-center text-sm text-muted-foreground">
            {hasUnconfirmed
              ? t("settings.plans.history.emptyPending")
              : t("settings.plans.history.empty")}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("settings.plans.history.columnDate")}</TableHead>
                <TableHead>{t("settings.plans.history.columnPlan")}</TableHead>
                <TableHead className="text-right">
                  {t("settings.plans.history.columnAmount")}
                </TableHead>
                <TableHead>{t("settings.plans.history.columnStatus")}</TableHead>
                <TableHead className="w-[120px] text-right">
                  {t("settings.plans.history.columnPdf")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const status = resolveStatus(row);
                const intervalLabel =
                  row.billingInterval === "annual"
                    ? t("settings.plans.history.intervalAnnual")
                    : t("settings.plans.history.intervalMonthly");
                const canDownload =
                  row.invoiceStatus === "invoice_issued" &&
                  row.invoice?.dgiStatus === "confirmed";

                return (
                  <TableRow key={row.chargeId}>
                    <TableCell className="whitespace-nowrap font-medium">
                      {formatDate(row.chargedAt)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-foreground">
                          {row.planName}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {intervalLabel}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right font-medium tabular-nums">
                      {formatAmount(row.amountMinor, row.currency)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={status.variant} className="font-medium">
                        {t(status.labelKey)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={!canDownload}
                        onClick={() => {
                          void downloadSubscriptionInvoicePdf(row.chargeId).catch(
                            (err) => {
                              toast.error(
                                err instanceof Error ? err.message : "Error",
                              );
                            },
                          );
                        }}
                      >
                        <Download className="mr-1.5 h-3.5 w-3.5" />
                        {t("settings.plans.history.downloadPdf")}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
